import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import CacheAPI from 'api/orbitV1/CacheAPI'
import SimAPI from 'api/orbitV1/SimAPI'
import BorderLinearProgress from 'common/ui/BorderLinearProgress'
import { ColorBoxContent, ColorBoxHeader } from 'common/ui/colorBox'
import { SimIcon } from 'common/ui/icons'
import { ERROR_ALLOCATION_EXCEEDED } from 'constants/errors'
import { splitArrayIntoRows } from 'helpers/array'
import { getIccid, getMsisdn, SimStatus } from 'helpers/simStatus'
import { convertBytesToGB, convertMinsToHrs } from 'helpers/usageConverters'
import useOrbitFlag from 'hooks/useOrbitFlag'
import {
  mockAppAccesses,
  mockBundleUsage,
  mockTotalTeamBundle
} from 'mock/mockData'

import AlertDialog from '../SimManagement/components/AlertDialog'
import ChartJsUsageChart from '../SimManagement/components/ChartJsUsageChart'

const STATUSES = SimStatus
const DEFAULTDATAUSAGE = [
  { x: 'January', y: 9234 },
  { x: 'February', y: 7328 },
  { x: 'March', y: 7239 },
  { x: 'April', y: 9356 },
  { x: 'May', y: 10003 },
  { x: 'June', y: 13392 },
  { x: 'July', y: 8343 },
  { x: 'August', y: 9234 },
  { x: 'September', y: null },
  { x: 'October', y: null },
  { x: 'November', y: null },
  { x: 'December', y: null }
]
const DEFAULTCALLUSAGE = [
  { x: 'January', y: 45 },
  { x: 'February', y: 30 },
  { x: 'March', y: 26 },
  { x: 'April', y: 5 },
  { x: 'May', y: 10 },
  { x: 'June', y: 33 },
  { x: 'July', y: 43 },
  { x: 'August', y: 13 },
  { x: 'September', y: null },
  { x: 'October', y: null },
  { x: 'November', y: null },
  { x: 'December', y: null }
]
const DEFAULTSMSUSAGE = [
  { x: 'January', y: 5 },
  { x: 'February', y: 7 },
  { x: 'March', y: 3 },
  { x: 'April', y: 9 },
  { x: 'May', y: 10 },
  { x: 'June', y: 5 },
  { x: 'July', y: 2 },
  { x: 'August', y: 4 },
  { x: 'September', y: null },
  { x: 'October', y: null },
  { x: 'November', y: null },
  { x: 'December', y: null }
]

export default function SimDetails() {
  const FlagUsageStats = useOrbitFlag('editSim-usageStats')

  const navigate = useNavigate()
  const location = useLocation()
  const teamMember = location.state.teamMember
  const dataOptions = [20, 40, 60, 80, 100, 200, 300]
  const voiceOptions = [100, 200, 300, 400, 500, 1000]
  const smsOptions = [100, 200, 300, 400, 500]
  const [bundle, setBundle] = useState({})

  const [name, setName] = useState('')
  const [remarks, setRemarks] = useState('')
  const [mobile, setMobile] = useState('')
  const [simStatus, setSimStatus] = useState('')
  const [allocatedData, setAllocatedData] = useState(0)
  const [allocatedVoice, setAllocatedVoice] = useState(0)
  const [allocatedSms, setAllocatedSms] = useState(0)
  const [allocatedDataError, setAllocatedDataError] = useState('')
  const [allocatedVoiceError, setAllocatedVoiceError] = useState('')
  const [allocatedSmsError, setAllocatedSmsError] = useState('')
  const [totalAllocatedData, setTotalAllocatedData] = useState(0)
  const [totalAllocatedVoice, setTotalAllocatedVoice] = useState(0)
  const [totalAllocatedSms, setTotalAllocatedSms] = useState(0)
  const [appAccesses, setAppAccesses] = useState([])

  const [usageData] = useState([
    DEFAULTDATAUSAGE,
    DEFAULTCALLUSAGE,
    DEFAULTSMSUSAGE
  ])

  const handleAllocatedDataChange = (e) => {
    const newAllocation = e.target.value
    const increaseInAllocation = newAllocation - allocatedData
    setAllocatedData(newAllocation * 1000000000)
    setTotalAllocatedData(totalAllocatedData + increaseInAllocation)
  }

  useEffect(() => {
    if (totalAllocatedData > bundle.data) {
      setAllocatedDataError(ERROR_ALLOCATION_EXCEEDED)
    } else {
      setAllocatedDataError('')
    }
  }, [totalAllocatedData])

  const handleAllocatedVoiceChange = (e) => {
    const newAllocation = e.target.value
    const increaseInAllocation = newAllocation - allocatedVoice
    setAllocatedVoice(newAllocation * 60)
    setTotalAllocatedVoice(totalAllocatedVoice + increaseInAllocation)
  }

  useEffect(() => {
    if (totalAllocatedVoice > bundle.voice) {
      setAllocatedVoiceError(ERROR_ALLOCATION_EXCEEDED)
    } else {
      setAllocatedVoiceError('')
    }
  }, [totalAllocatedVoice])

  const handleAllocatedSmsChange = (e) => {
    /* Assuming that total allocated sms is already including current allocated sms */
    const newAllocation = e.target.value
    const increaseInAllocation = newAllocation - allocatedSms
    setAllocatedSms(newAllocation)
    setTotalAllocatedSms(totalAllocatedSms + increaseInAllocation)
  }

  useEffect(() => {
    if (totalAllocatedSms > bundle.sms) {
      setAllocatedSmsError(ERROR_ALLOCATION_EXCEEDED)
    } else {
      setAllocatedSmsError('')
    }
  }, [totalAllocatedSms])

  const handleSimStatusChange = (e) => {
    const {
      target: { value }
    } = e
    setSimStatus(value)
  }

  const [isDialogOpen, setDialogOpen] = useState(false)
  // commented for onboarding
  // const handleSimAlert = () => {
  //   if (simStatus !== teamMember.status || name !== teamMember.owner) {
  //     setDialogOpen(true)
  //   }
  // }

  const handleSaveChanges = async () => {
    setDialogOpen(false)

    try {
      await CacheAPI.setCache({
        id: teamMember._id,
        simOwner: name,
        simRemarks: remarks
      })
      const setResponse = await SimAPI.updateSim({
        iccid: teamMember.iccid,
        owner: name,
        remarks
      })
      if (setResponse.data) {
        toast.success('The sim details have been updated.', {
          autoClose: 3000,
          hideProgressBar: false
        })

        setTimeout(() => {
          navigate(-1)
        }, 3000)
      } else {
        toast.error(
          'There was an issue updating your sim details, please try again later.'
        )
      }
    } catch (error) {
      throw new Error(error?.response?.message || error?.message)
    }
  }
  /* data/voice converters */

  useEffect(() => {
    setName(teamMember.simOwner || teamMember.owner)
    setRemarks(teamMember.simRemarks)
    setMobile(getMsisdn(teamMember.msisdn))
    setSimStatus(teamMember.status)
    setAllocatedData(teamMember.cacheData || teamMember.data)
    setAllocatedVoice(teamMember.cacheVoice || teamMember.voice)
    setAllocatedSms(teamMember.cacheSms || teamMember.sms)
    setBundle(mockTotalTeamBundle)
    const appAccessRows = splitArrayIntoRows(mockAppAccesses, 2)
    setAppAccesses(appAccessRows)
  }, [])

  useEffect(() => {
    setTotalAllocatedData(bundle.data - mockBundleUsage.remainingData)
    setTotalAllocatedVoice(bundle.voice - mockBundleUsage.remainingVoice)
    setTotalAllocatedSms(bundle.sms - mockBundleUsage.remainingSms)
  }, [bundle])

  /* Get usage data */
  useEffect(() => {
    const getData = async () => {
      /* Use mock data instead of actual data for now */
      // let { data } = await UsageAPI.getUsage('89228308')
      /* setUsageData([
        calLabels.map((month, index) => {
          return {
            x: month,
            y: data[index]['voice']
          }
        }),

        calLabels.map((month, index) => {
          return {
            x: month,
            y: data[index]['sms']
          }
        }),

        calLabels.map((month, index) => {
          return {
            x: month,
            y: data[index]['data']
          }
        })
      ]) */
    }
    getData()
  }, [])

  const calLabels = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  // feature flags
  const allocationBox = useOrbitFlag('editSim-allocationBox')
  const appAccessBox = useOrbitFlag('editSim-appAccessBox')

  return (
    <>
      <ColorBoxHeader>Manage Access</ColorBoxHeader>
      <ColorBoxContent width='100%' style={{ marginTop: 16 }}>
        <Typography variant='h5' sx={{ marginLeft: 0 }}>
          Member Details
        </Typography>
        <Box
          sx={{
            display: 'flex',
            marginBottom: '24px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%',
              marginRight: '40px'
            }}
          >
            <TextField
              fullWidth
              variant='standard'
              label='Name'
              id='nameTextField'
              value={name}
              sx={{ width: '90%' }}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%'
            }}
          >
            <TextField
              fullWidth
              variant='standard'
              label='Mobile Number'
              id='mobileTextField'
              value={mobile}
              sx={{ width: '90%', background: 'white' }}
              disabled={mobile ? true : false}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            marginBottom: '24px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <TextField
              fullWidth
              variant='standard'
              label='Remarks'
              id='remarksTextField'
              value={remarks}
              sx={{ width: '95%' }}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </Box>
        </Box>

        <Typography variant='h5' sx={{ marginLeft: 0 }}>
          SIM Details
        </Typography>
        <Box
          sx={{ display: 'flex', marginBottom: '24px', alignItems: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              marginRight: '48px',
              width: '50%'
            }}
          >
            <TextField
              fullWidth
              variant='standard'
              label='ICCID'
              id='iccidTextField'
              value={getIccid(teamMember.iccid)}
              sx={{
                width: '90%',
                background: 'white',
                marginTop: '24px'
              }}
              disabled={teamMember.iccid}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '50%'
            }}
          >
            <FormLabel>Status</FormLabel>
            <Select
              label='status'
              value={simStatus || 'Suspended'}
              id='statusSelectField'
              onChange={handleSimStatusChange}
              sx={{ width: '90%', background: 'white' }}
              disabled
            >
              {Object.entries(STATUSES).map(([key, value]) => (
                <MenuItem key={key} value={value}>{value}</MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        {FlagUsageStats && (
          <>
            <Typography variant='h5'>Usage Statistics</Typography>
            {usageData.length > 0 && (
              <ChartJsUsageChart data={usageData} calLabels={calLabels} />
            )}
          </>
        )}

        {allocationBox && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '32px',
              gap: '10px'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '50%'
              }}
            >
              <Typography variant='h5' sx={{ margin: '24px 0' }}>
                Allocation
              </Typography>
              <Box
                sx={{
                  marginBottom: '24px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FormLabel>Data /GB</FormLabel>
                <Select
                  value={convertBytesToGB(totalAllocatedData - allocatedData)}
                  id='dataSelectField'
                  onChange={handleAllocatedDataChange}
                  sx={{ width: '90%' }}
                >
                  {dataOptions.map((option) => (
                    <MenuItem key={option}>{option}</MenuItem>
                  ))}
                </Select>
                {allocatedDataError && (
                  <Typography variant='note'>{allocatedDataError}</Typography>
                )}
              </Box>
              <Box
                sx={{
                  marginBottom: '24px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FormLabel>Voice /min</FormLabel>
                <Select
                  value={convertMinsToHrs(totalAllocatedVoice - allocatedVoice)}
                  id='voiceSelectField'
                  onChange={handleAllocatedVoiceChange}
                  sx={{ width: '90%' }}
                >
                  {voiceOptions.map((option) => (
                    <MenuItem key={option}>{option}</MenuItem>
                  ))}
                </Select>
                {allocatedVoiceError && (
                  <Typography variant='note'>{allocatedVoiceError}</Typography>
                )}
              </Box>
              <Box
                sx={{
                  marginBottom: '24px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <FormLabel>SMS</FormLabel>
                <Select
                  value={totalAllocatedSms - allocatedSms}
                  id='smsSelectField'
                  onChange={handleAllocatedSmsChange}
                  sx={{ width: '90%' }}
                >
                  {smsOptions.map((option) => (
                    <MenuItem key={option}>{option}</MenuItem>
                  ))}
                </Select>
                {allocatedSmsError && (
                  <Typography variant='note'>{allocatedSmsError}</Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '50%'
              }}
            >
              <ColorBoxContent variant='secondary' style={{ width: '90%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: 'primary.main',
                    padding: `4px 24px`
                  }}
                >
                  <Typography variant='h5' sx={{ color: 'white' }}>
                    Current Usage for Month
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '20px 0'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '24px'
                    }}
                  >
                    <Box sx={{ margin: '4px 12px' }}>
                      <SimIcon />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}
                    >
                      <Typography
                        sx={{
                          color: allocatedDataError ? 'error.main' : '',
                          margin: '0 8px'
                        }}
                      >
                        Data
                      </Typography>
                      <BorderLinearProgress
                        width='100%'
                        current={convertBytesToGB(allocatedData)}
                        total={convertBytesToGB(bundle.data)}
                        sx={{
                          alignItems: 'center',
                          color: allocatedDataError ? 'error.main' : '',
                          margin: `0 8px`
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '24px',
                      justifyContent: 'center'
                    }}
                  >
                    <Box sx={{ margin: '4px 12px' }}>
                      <SimIcon />
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%'
                      }}
                    >
                      <Typography
                        sx={{
                          color: allocatedVoiceError ? 'error.main' : ''
                        }}
                      >
                        Voice
                      </Typography>
                      <BorderLinearProgress
                        width='100%'
                        current={convertMinsToHrs(allocatedVoice)}
                        total={convertMinsToHrs(bundle.voice)}
                        style={{
                          alignItems: 'center',
                          color: allocatedVoiceError ? 'error.main' : '',
                          margin: `0 8px`
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ margin: '4px 12px' }}>
                      <SimIcon />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography
                        sx={{
                          color: allocatedSmsError ? 'error.main' : '',
                          margin: `0 8px`
                        }}
                      >
                        SMS
                      </Typography>
                      <Typography
                        sx={{
                          color: allocatedSmsError ? 'error.main' : '',
                          margin: `0 8px`
                        }}
                      >
                        {allocatedSms} / {bundle.sms} SMS
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ColorBoxContent>
            </Box>
          </Box>
        )}

        {appAccessBox && (
          <Box>
            <Typography variant='h5'>App Access</Typography>
            {appAccesses.map((accessRow, id) => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}
                key={id}
              >
                {accessRow.map((access) => (
                  <Box
                    width='50%'
                    key={access.app}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderBottom: `${
                        id !== appAccesses.length - 1 ? '1px solid #B8C4D3' : ''
                      }`
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {access.icon}
                      <Typography sx={{ marginLeft: '20px' }}>
                        <b>{access.app}</b>
                      </Typography>
                    </Box>
                    <Switch defaultChecked={access.allowAccess} />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        )}

        <Box display='flex' justifyContent='flex-end' width='100%'>
          <Button
            id='cancelBtn'
            variant='secondary'
            sx={{ backgroundColor: 'transparent' }}
            onClick={() => {
              navigate(-1)
            }}
          >
            Cancel
          </Button>
          <Button
            id='saveChangesBtn'
            variant='filled'
            onClick={
              handleSaveChanges
            } /* {handleSimAlert} disabled for onboarding */
            disabled={
              allocatedDataError || allocatedVoiceError || allocatedSmsError
            }
          >
            Save Changes
          </Button>
        </Box>
      </ColorBoxContent>
      <AlertDialog
        teamMember={teamMember}
        isDialogOpen={isDialogOpen}
        setDialogOpen={setDialogOpen}
        handleSaveChanges={handleSaveChanges}
        simStatus={simStatus}
      />
    </>
  )
}
